import { useCallback, useMemo } from "react"
import PropTypes from "prop-types"
import { map, isObject, isNil, isFunction, isUndefined } from "lodash"
import classNames from "classnames"
import { TableRow, TableCell, Checkbox, Link } from "@mui/material"
import { KeyboardArrowLeft, Link as LinkIcon } from "@mui/icons-material"
import { formatLabel, treatAsFactor } from "helpers/securities"
import { TABLE_GROUP_DROPDOWN } from "helpers/shapes"
import { ITEM, GROUP_ITEM, COLUMN_GROUP } from "./shapes"
import TableDropdown from "components/table-dropdown"
import CellTooltip from "../cell-tooltip"
import PositionTooltip from "components/position-tooltip"
import { recursiveStickyCheck } from "../helpers"
import EditableField from "../editable-field"
import "./styles.scss"

export function getCellContents(getter, item, id, columnId) {
  const contents = getter(item, id, columnId)
  let value, tooltipContents
  if (isObject(contents)) {
    ({ value, tooltipContents } = contents)
  } else {
    value = contents
  }
  return { value, tooltipContents }
}

export default function Row(props) {
  const {
    item,
    columns: columnGroups,
    onSelect,
    openSecurityProfile,
    openFactorProfile,
    openCompositeSecurity,
    rowIdClickOverride,
    selected,
    disableRowSelection,
    groupDropdown,
    dispatch,
    isActive,
    onRowClick,
    onChange,
    firstRow,
    isOriginalRow = true,
  } = props

  const handleRowClick = useCallback((event, row) => {
    if (!isFunction(onRowClick)) {
      return
    }
    onRowClick(event, row)
  }, [onRowClick])

  const selectSecurity = useCallback(() => {
    onSelect(item)
  }, [item, onSelect])

  const onRowIdClick = useCallback(() => {
    const { id: securityId, assetClass, ticker, mic, isGroup } = item

    if (isGroup) {
      if (isFunction(rowIdClickOverride)) {
        rowIdClickOverride(item)
      }
    } else if (treatAsFactor(assetClass)) {
      openFactorProfile(securityId)
    } else if (assetClass === "index") {
      openCompositeSecurity({ type: item.assetClass, id: item.id })
    } else {
      openSecurityProfile(assetClass, securityId, ticker, mic)
    }
  }, [item, openCompositeSecurity, openFactorProfile, openSecurityProfile, rowIdClickOverride])

  const getItem = useCallback(({ formatter, value, item, makeClick }) => {
    // This allows the selector passing the items to the table to control if the element is clickable or not
    const click = makeClick && makeClick(item)
    const displayValue = isFunction(formatter) ? formatter(value) : value
    if (click && !isNil(value)) {
      return <Link
        component="button"
        onClick={(e) => { e.preventDefault(); dispatch(click(item)) }}
        underline="hover"
      >
        {displayValue}
      </Link>
    }
    return displayValue
  }, [dispatch])

  const { id: securityId, label, isDrillDown, action, isGroup, isUnmodeled, isNoLongerHeld, isNotHeld, isAggregate, isEditable, pinned, classnames } = item
  const { columns: groupColumns, items: groupItems, selectedId: groupSelectedId, backAction } = groupDropdown ?? {}

  const formattedIdentifier = useMemo(() =>
    (<div className="row-label">{label || formatLabel(item)}</div>),
  [item, label])

  const rowLabel = useMemo(() => {
    if (isDrillDown) {
      return <div className="id drill-down-link" href="#">
        <a className="back-button" onClick={() => dispatch(backAction)}><KeyboardArrowLeft /></a>
        <TableDropdown
          label={formattedIdentifier}
          columns={groupColumns}
          items={groupItems}
          selectedItemId={groupSelectedId}
          onItemClick={(group) => dispatch(action(group.id))}
          isActive={isActive}
        />
      </div>
    }
    return isAggregate || isUnmodeled || !isOriginalRow ? formattedIdentifier : <a className="id" href="#" onClick={onRowIdClick}>{formattedIdentifier}{!isGroup && <LinkIcon className="icon" />}</a>
  }, [action, backAction, dispatch, formattedIdentifier, groupColumns, groupItems, groupSelectedId, isAggregate, isDrillDown, isGroup, isUnmodeled, onRowIdClick, isActive, isOriginalRow])
  const content = useMemo(() => {
    return <TableRow className={classNames({ "aggregation-row": isAggregate, "drill-down": isDrillDown, group: isGroup, "not-held": isNotHeld, "no-longer-held": isNoLongerHeld, "is-editing": isEditable, pinned, ...classnames })} onClick={(e) => handleRowClick(e, item)} key={securityId}>
      <PositionTooltip
        position={!isAggregate && isOriginalRow ? item : null}
        isActive={isActive}
        classes={{ popper: "security-description-container force", tooltip: "security-description" }}
        noWrapper
      >
        <TableCell className={classNames("group-end identifier", { "sticky-left": recursiveStickyCheck(columnGroups), "display-name-unmodeled": isUnmodeled, "first-table-cell": firstRow })} size="small">
          <div className="checkbox-wrapper">
            {!disableRowSelection && !isAggregate && <Checkbox color="primary" className="watchlist-checkbox" checked={selected} onChange={selectSecurity} />}
            {rowLabel}
          </div>
        </TableCell>
      </PositionTooltip>

      {/* columnGroups: Columns are typically grouped by metric/category: e.g 'Risk', 'Performance'
          groupColumns: When using groupBy, a group has a set of sub-columns
          columns: Columns contained inside of a group */}
      {map(columnGroups, ({ category, getter: groupGetter, columns }) => map(columns, (props, index) => {
        const { id, path, formatter, makeClick, isSticky, editable: columnIsEditable, getter: columnGetter } = props
        const { value, tooltipContents } = getCellContents(columnGetter ?? groupGetter, item, path || id, id)
        const content = getItem({ formatter, value, item, makeClick })
        let tableCellContent = !tooltipContents ? content
        : <div className="cell-content-wrapper">
          <CellTooltip contents={tooltipContents} />
          {content}
        </div>
        if (columnIsEditable && isEditable) {
          tableCellContent = <EditableField item={item} id={id} content={content} onChange={onChange} />
        }
        const editable = columnIsEditable && isUndefined(isEditable) && !isAggregate
        return <TableCell
          key={`${securityId}${category}${id}`}
          size="small"
          align="right"
          className={classNames(id, { "group-start": index === 0, "sticky-left": isSticky, editable })}
        >
          <span className={classNames("cell-text")} data-id={editable ? id : undefined}>{tableCellContent}</span>
        </TableCell>
      }),
      )}
    </TableRow>
  }, [isAggregate, isDrillDown, isGroup, isNotHeld, isNoLongerHeld, isEditable, pinned, classnames, securityId, columnGroups, isUnmodeled, firstRow, item, isActive, disableRowSelection, selected, selectSecurity, rowLabel, handleRowClick, getItem, onChange, isOriginalRow])

  return content
}

Row.propTypes = {
  item: PropTypes.oneOfType([ITEM, GROUP_ITEM]),
  columns: PropTypes.arrayOf(COLUMN_GROUP),
  selected: PropTypes.bool,
  disableRowSelection: PropTypes.bool,
  isDrillDown: PropTypes.bool,
  isActive: PropTypes.bool,
  groupDropdown: TABLE_GROUP_DROPDOWN,
  onChange: PropTypes.func,
  onSelect: PropTypes.func,
  openSecurityProfile: PropTypes.func,
  openFactorProfile: PropTypes.func,
  openCompositeSecurity: PropTypes.func,
  rowIdClickOverride: PropTypes.func,
  dispatch: PropTypes.func,
  onRowClick: PropTypes.func,
  firstRow: PropTypes.bool,
  isOriginalRow: PropTypes.bool,
}
